import React from 'react';
import { useField, ErrorMessage } from 'formik';
import classNames from 'classnames';
import DOMPurify from 'dompurify';

interface FormTextAreaFieldProps {
    fieldName: string;
    label: string;
    readOnly?: boolean;
    required?: boolean;
    small?: boolean;
    textAreaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
    tooltipIconData?: {
        mainIcon: string;
        closeIcon: string;
    };
    tooltip?: string | null;
    renderTooltipTextAsHtml?: boolean;
    customClassName?: string;
}

export const FormTextAreaField: React.FC<FormTextAreaFieldProps> = ({
    fieldName,
    label,
    readOnly = false,
    required = false,
    small = false,
    textAreaProps = {},
    tooltipIconData,
    tooltip,
    renderTooltipTextAsHtml = false,
    customClassName,
}) => {
    const validate = required
        ? (value: string) => (!value?.trim() ? `${label} is required.` : undefined)
        : undefined;

    const [field, meta] = useField({ name: fieldName, validate });

    const showError = meta.touched && meta.error;
    const classnames = classNames('TextArea', { ViewOnly: readOnly });

    return (
        <div
            className={classNames(
                `${
                    customClassName
                        ? customClassName + ' FormBox FormSideBySide'
                        : 'FormBox FormSideBySide'
                }`,
                {
                    FormFieldRequired: required,
                }
            )}
        >
            <div className="FormLabel">
                <label htmlFor={fieldName}>{label}</label>
                {tooltip && tooltipIconData?.mainIcon && tooltipIconData?.closeIcon && (
                    <div className="FormTooltipContainer">
                        <div
                            className="TooltipIconContainer"
                            onClick={() => DOMPurify.sanitize(tooltip)}
                        >
                            <img
                                src={tooltipIconData.mainIcon}
                                alt="Tooltip icon"
                                className="TooltipMainIcon"
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="FormField">
                <textarea
                    id={fieldName}
                    {...field}
                    {...textAreaProps}
                    className={classnames}
                    readOnly={readOnly}
                />
                {showError && <div className="ErrorLabel">{meta.error}</div>}
            </div>
        </div>
    );
};
